/**
 * ProductDateCacheContext
 * 
 * Caches key dates for the current product:
 * - Release end dates (planned + projected 18 months forward)
 * - Initiative target dates
 * - Goal target dates
 * 
 * Used by DatePickerWithMarkers to highlight important dates.
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { releasesApi, initiativesApi, goalsApi } from '../services/api';
import { addDays, format, parseISO, isAfter, isBefore, addMonths } from 'date-fns';

const ProductDateCacheContext = createContext(null);

export const useProductDateCache = () => {
  const context = useContext(ProductDateCacheContext);
  if (!context) {
    throw new Error('useProductDateCache must be used within a ProductDateCacheProvider');
  }
  return context;
};

export const ProductDateCacheProvider = ({ children }) => {
  const [cache, setCache] = useState({
    productId: null,
    releases: {
      settings: { sprint_length_days: 14, first_sprint_start: null },
      planned: [],
      projected: [],
    },
    initiatives: [],
    goals: [],
    loading: false,
    loaded: false,
  });

  /**
   * Calculate projected release end dates for 18 months forward
   */
  const calculateProjectedReleases = useCallback((settings, plannedReleases) => {
    const { sprint_length_days, first_sprint_start } = settings;
    
    if (!first_sprint_start || !sprint_length_days) {
      return [];
    }

    const projectedReleases = [];
    const horizon = addMonths(new Date(), 18); // 18 months forward
    const firstStart = parseISO(first_sprint_start);
    
    // Find the last planned release to continue from
    let lastPlannedEndDate = null;
    let lastPlannedNumber = 0;
    
    if (plannedReleases.length > 0) {
      const sortedPlanned = [...plannedReleases].sort((a, b) => {
        if (!a.end_date || !b.end_date) return 0;
        return new Date(b.end_date) - new Date(a.end_date);
      });
      const lastPlanned = sortedPlanned[0];
      if (lastPlanned?.end_date) {
        lastPlannedEndDate = parseISO(lastPlanned.end_date);
        lastPlannedNumber = lastPlanned.release_number || plannedReleases.length;
      }
    }

    // Calculate starting point for projections
    let currentReleaseNumber = lastPlannedNumber + 1;
    let currentStartDate;
    
    if (lastPlannedEndDate) {
      // Start from day after last planned release ends
      currentStartDate = addDays(lastPlannedEndDate, 1);
    } else {
      // No planned releases, start from first sprint start
      currentStartDate = firstStart;
      currentReleaseNumber = 1;
    }

    // Generate projected releases until horizon
    while (isBefore(currentStartDate, horizon)) {
      const endDate = addDays(currentStartDate, sprint_length_days - 1);
      
      // Only add if end date is in the future
      if (isAfter(endDate, new Date())) {
        projectedReleases.push({
          name: `Release ${currentReleaseNumber}`,
          release_number: currentReleaseNumber,
          start_date: format(currentStartDate, 'yyyy-MM-dd'),
          end_date: format(endDate, 'yyyy-MM-dd'),
          isProjected: true,
        });
      }
      
      currentStartDate = addDays(endDate, 1);
      currentReleaseNumber++;
      
      // Safety limit
      if (projectedReleases.length > 50) break;
    }

    return projectedReleases;
  }, []);

  /**
   * Load all date-relevant data for a product
   */
  const loadProductDates = useCallback(async (productId) => {
    if (!productId) return;
    
    // Skip if already loaded for this product
    if (cache.productId === productId && cache.loaded) {
      return;
    }

    setCache(prev => ({ ...prev, loading: true }));

    try {
      // Fetch all data in parallel
      const [releasesRes, settingsRes, initiativesRes, goalsRes] = await Promise.all([
        releasesApi.list(productId).catch(() => ({ data: [] })),
        releasesApi.getSprintSettings(productId).catch(() => ({ data: {} })),
        initiativesApi.listByProduct(productId).catch(() => ({ data: [] })),
        goalsApi.list(productId).catch(() => ({ data: [] })),
      ]);

      const plannedReleases = releasesRes.data || [];
      const settings = settingsRes.data || { sprint_length_days: 14, first_sprint_start: null };
      const initiatives = initiativesRes.data || [];
      const goals = goalsRes.data || [];

      // Calculate projected releases
      const projectedReleases = calculateProjectedReleases(settings, plannedReleases);

      setCache({
        productId,
        releases: {
          settings,
          planned: plannedReleases.map(r => ({
            ...r,
            isProjected: false,
          })),
          projected: projectedReleases,
        },
        initiatives: initiatives.filter(i => i.target_date),
        goals: goals.filter(g => g.target_date),
        loading: false,
        loaded: true,
      });
    } catch (error) {
      console.error('[ProductDateCache] Error loading product dates:', error);
      setCache(prev => ({ ...prev, loading: false }));
    }
  }, [cache.productId, cache.loaded, calculateProjectedReleases]);

  /**
   * Refresh release dates (called when settings change or release created)
   */
  const refreshReleaseDates = useCallback(async (productId) => {
    if (!productId) return;

    try {
      const [releasesRes, settingsRes] = await Promise.all([
        releasesApi.list(productId),
        releasesApi.getSprintSettings(productId),
      ]);

      const plannedReleases = releasesRes.data || [];
      const settings = settingsRes.data || cache.releases.settings;
      const projectedReleases = calculateProjectedReleases(settings, plannedReleases);

      setCache(prev => ({
        ...prev,
        releases: {
          settings,
          planned: plannedReleases.map(r => ({ ...r, isProjected: false })),
          projected: projectedReleases,
        },
      }));
    } catch (error) {
      console.error('[ProductDateCache] Error refreshing release dates:', error);
    }
  }, [cache.releases.settings, calculateProjectedReleases]);

  /**
   * Get all release end dates (planned + projected) for calendar highlighting
   */
  const getAllReleaseEndDates = useMemo(() => {
    const dates = [];
    
    // Planned releases
    cache.releases.planned.forEach(r => {
      if (r.end_date) {
        dates.push({
          date: r.end_date,
          label: r.name || `Release ${r.release_number}`,
          type: 'release_planned',
          color: 'blue',
        });
      }
    });

    // Projected releases
    cache.releases.projected.forEach(r => {
      if (r.end_date) {
        dates.push({
          date: r.end_date,
          label: r.name,
          type: 'release_projected',
          color: 'lightblue',
        });
      }
    });

    return dates;
  }, [cache.releases.planned, cache.releases.projected]);

  /**
   * Get initiative by ID
   */
  const getInitiative = useCallback((initiativeId) => {
    return cache.initiatives.find(i => i.initiative_id === initiativeId);
  }, [cache.initiatives]);

  /**
   * Get goals by IDs (for Initiative's parent goals)
   */
  const getGoalsByIds = useCallback((goalIds) => {
    if (!goalIds || !Array.isArray(goalIds)) return [];
    return cache.goals.filter(g => goalIds.includes(g.goal_id));
  }, [cache.goals]);

  const value = useMemo(() => ({
    cache,
    loadProductDates,
    refreshReleaseDates,
    getAllReleaseEndDates,
    getInitiative,
    getGoalsByIds,
    initiatives: cache.initiatives,
    goals: cache.goals,
  }), [cache, loadProductDates, refreshReleaseDates, getAllReleaseEndDates, getInitiative, getGoalsByIds]);

  return (
    <ProductDateCacheContext.Provider value={value}>
      {children}
    </ProductDateCacheContext.Provider>
  );
};

export default ProductDateCacheContext;
